import {
  CascaderField,
  DropdownSubmitButton,
  Form,
  Loading,
  MonetaryInputField,
  SelectField,
  SubmitButton,
  TextAreaInputField,
} from '@/components';
import {
  useBudgetTypes,
  useExpenditureBudgetPurpose,
  useNestedExpenditureBudgetPurposeOptions,
} from '@/features/master-table/hooks';
import type { AccountingTypeModel } from '@/features/master-table/models';
import { getExpenditureBudgetPurposeConcatData } from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import { convertBudgetTypeNameToOrderingKey } from '@/features/project/_utils/budget-and-execution';
import { ExpenditureItemFormSchema } from '@/features/project/projects/_models/expenditure-item';
import { useForm, useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import type { Recursive } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof ExpenditureItemFormSchema>;
type CreateExpenditureItemNextDrawerKey = 'expenditure-item';

type Props = {
  fiscalYear: number;
  accountingType?: z.infer<typeof AccountingTypeModel>;
  accountTitleId?: string | null;
  initialValues?: Partial<FormValues>;
  onSubmit: ({
    values,
    nextDrawerKey,
    onSuccess,
  }: {
    values: FormValues;
    nextDrawerKey?: CreateExpenditureItemNextDrawerKey;
    onSuccess?: () => void;
  }) => Promise<void>;
  isEditing?: boolean;
  existBudgetTypeIds?: string[];
};

export const ExpenditureItemForm: FC<Props> = ({
  fiscalYear,
  accountingType,
  accountTitleId,
  initialValues,
  onSubmit,
  isEditing,
  existBudgetTypeIds,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { id } = useId();
  const project = useProject(id);
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    watch,
    setValue,
    resetField,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      ...initialValues,
      expenditureBudgetPurposeConstituentIds: initialValues
        ?.expenditureBudgetPurposeConstituentIds?.length
        ? initialValues.expenditureBudgetPurposeConstituentIds
        : undefined,
    },
    resolver: zodResolver(ExpenditureItemFormSchema),
  });
  const budgetTypes = useBudgetTypes({
    params: {
      fiscalYear: fiscalYear.toString(),
    },
  });

  const isSkipAdministrativeReformTeamEvaluation =
    !!project.data?.skipAdministrativeReformTeamEvaluation;

  const expenditureBudgetPurpose = useNestedExpenditureBudgetPurposeOptions({
    params: {
      type: isSkipAdministrativeReformTeamEvaluation
        ? 'majorMatter'
        : 'revenueNature',
      referenceFiscalYear: fiscalYear.toString(),
      accountingId: accountingType?.id ?? undefined,
      accountTitleId: accountTitleId ?? undefined,
    },
    options: { hasChildrenExpenditurePurpose: true },
    enabled: !isSkipAdministrativeReformTeamEvaluation,
  });

  const majorExpenditureBudgetPurpose = useExpenditureBudgetPurpose({
    id: project.data?.majorExpenditureBudgetPurposeId ?? '',
    enabled: isSkipAdministrativeReformTeamEvaluation,
  });

  const expenditureBudgetPurposeOptions = useMemo<
    Recursive<{ label: string; value: string }>[]
  >(() => {
    if (!isSkipAdministrativeReformTeamEvaluation) {
      return expenditureBudgetPurpose.data ?? [];
    }
    const majorExpenditureBudgetPurposeData =
      majorExpenditureBudgetPurpose.data;

    return majorExpenditureBudgetPurposeData
      ? getExpenditureBudgetPurposeConcatData({
          data: majorExpenditureBudgetPurposeData,
          withoutAccountingName: true,
        }).options
      : [];
  }, [
    expenditureBudgetPurpose.data,
    majorExpenditureBudgetPurpose.data,
    isSkipAdministrativeReformTeamEvaluation,
  ]);

  const [budgetTypeId, expenditureBudgetPurposeConstituentIds] = watch([
    'budgetTypeId',
    'expenditureBudgetPurposeConstituentIds',
  ]);
  const budgetType = budgetTypes.data?.find((d) => d.id === budgetTypeId);

  if (
    (!expenditureBudgetPurposeOptions.length &&
      project.data?.majorExpenditureBudgetPurposeId) ||
    budgetTypes.isPending ||
    !budgetTypes.data ||
    !accountingType
  ) {
    return <Loading />;
  }

  const budgetTypeOptions = budgetTypes.data
    .flatMap((d) =>
      (d.type !== 'carried-forward' && d.type !== 'reserve') ||
      !existBudgetTypeIds?.some((budgetTypeId) => budgetTypeId === d.id) ||
      isEditing
        ? {
            label: d.name,
            value: d.id,
          }
        : [],
    )
    .sort((a, b) => {
      return (
        convertBudgetTypeNameToOrderingKey(a.label) -
        convertBudgetTypeNameToOrderingKey(b.label)
      );
    });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(async (values: FormValues) => {
        await onSubmit({
          values,
        });
      })}
    >
      <SelectField
        name="budgetTypeId"
        label={t('budget-type')}
        options={budgetTypeOptions}
        control={control}
        errors={errors}
        disabled={isEditing}
        required
        shouldCoexistOnChange
        onChange={(value) => {
          const nextBudgetType = budgetTypes.data?.find((d) => d.id === value);
          if (nextBudgetType) {
            setValue('budgetType', nextBudgetType.type);
          }
          if (nextBudgetType?.type !== 'initial') {
            resetField('expenditureBudgetPurposeConstituentIds', {
              keepDirty: false,
              keepTouched: false,
              keepError: false,
              defaultValue: null,
            });
            resetField('requestedAmount', {
              keepDirty: false,
              keepTouched: false,
              keepError: false,
              defaultValue: initialValues?.requestedAmount,
            });
          }
        }}
      />

      <CascaderField
        name="expenditureBudgetPurposeConstituentIds"
        label={
          isSkipAdministrativeReformTeamEvaluation
            ? t('expenditure-purpose-level-3-1')
            : t('expenditure-budget-purpose')
        }
        hint={
          !isSkipAdministrativeReformTeamEvaluation
            ? t('hint-expenditure-budget-purpose')
            : undefined
        }
        control={control}
        options={expenditureBudgetPurposeOptions}
        errors={errors}
        disabled={
          budgetType?.type === 'reserve' ||
          budgetType?.type === 'carried-forward'
        }
        shouldCoexistOnChange
        onChange={(value) => {
          if (value) {
            resetField('expenditureBudgetPurposeDescription', {
              keepDirty: false,
              keepTouched: false,
              keepError: false,
              defaultValue: null,
            });
          }
        }}
      />

      {(budgetType?.type === 'initial' ||
        budgetType?.type === 'supplementary') && (
        <TextAreaInputField
          name="expenditureBudgetPurposeDescription"
          label={t('expenditure-budget-purpose-description')}
          hint={t('hint-expenditure-budget-purpose-description')}
          control={control}
          errors={errors}
          rows={3}
          disabled={!!expenditureBudgetPurposeConstituentIds}
        />
      )}

      <MonetaryInputField
        name="finalizedAmount"
        control={control}
        label={t('finalized-amount')}
        errors={errors}
        unit={unit}
      />

      <MonetaryInputField
        name="requestedAmount"
        control={control}
        label={t('next-request-amount')}
        hint={t('hint-requested-amount')}
        errors={errors}
        unit={unit}
        disabled={budgetType?.type !== 'initial'}
      />

      <TextAreaInputField
        name="remark"
        label={t('expenditure-item-remark')}
        hint={
          isSkipAdministrativeReformTeamEvaluation
            ? t('hint-expenditure-item-remark-for-skipped-review')
            : t('hint-expenditure-item-remark')
        }
        control={control}
        errors={errors}
        rows={3}
      />

      {isEditing ? (
        <SubmitButton loading={isSubmitting} disabled={!isDirty} />
      ) : (
        <DropdownSubmitButton<CreateExpenditureItemNextDrawerKey>
          loading={isSubmitting}
          menuItems={[
            {
              key: 'expenditure-item',
              label: t('save-and-create-next-item', {
                item: project.data?.majorExpenditureBudgetPurposeId
                  ? `${t('budget-type')}/${t('expenditure-purpose-level-3-1')}`
                  : `${t('budget-type')}/${t('expenditure-budget-purpose')}`,
              }),
            },
          ]}
          onClick={(e) => {
            handleSubmit(async (values) => {
              await onSubmit({
                values,
                nextDrawerKey: e.key,
                onSuccess: () => {
                  reset(initialValues);
                },
              });
            })();
          }}
        />
      )}
    </Form>
  );
};
